import { CreditCard, MoreVertical, SquarePen, Trash2 } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { Empty } from '@/components/ui/empty';
import { DEFAULT_PAGE_SIZE } from '@/constants/pagination';
import type { CustomerInvoice } from '@/apis/invoices';
import {
  formatListDate,
  formatMoney,
  getInvoiceBalance,
  getInvoiceListStatusDisplay,
} from './invoice-shared';

export type InvoiceRowAction = 'view' | 'edit' | 'payment' | 'delete';

interface Props {
  rows: CustomerInvoice[];
  isLoading: boolean;
  onRowAction: (action: InvoiceRowAction, invoice: CustomerInvoice) => void;
  selectedIds: Set<string>;
  onToggleRow: (id: string) => void;
  onToggleAll: () => void;
}

const COLUMNS = [
  'Date',
  'Invoice#',
  'Order Number',
  'Customer Name',
  'Invoice Status',
  'Due Date',
  'Invoice Amount',
  'Balance',
  'Company Name',
  'Action',
] as const;

const checkboxClass = 'h-4 w-4 rounded border-dark5 accent-primary-s500';

export function InvoiceTable({
  rows,
  isLoading,
  onRowAction,
  selectedIds,
  onToggleRow,
  onToggleAll,
}: Props) {
  if (!isLoading && rows.length === 0) {
    return <Empty />;
  }

  const allSelected = rows.length > 0 && rows.every((r) => selectedIds.has(r.id));

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-10">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={onToggleAll}
              aria-label="Select all invoices on this page"
              className={checkboxClass}
            />
          </TableHead>
          {COLUMNS.map((h) => (
            <TableHead key={h} className="text-xs font-medium tracking-wide text-lightGray whitespace-nowrap">
              {h}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {isLoading
          ? Array.from({ length: DEFAULT_PAGE_SIZE }).map((_, i) => (
              <TableRow key={i}>
                {Array.from({ length: COLUMNS.length + 1 }).map((_, j) => (
                  <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                ))}
              </TableRow>
            ))
          : rows.map((inv) => {
              const style = getInvoiceListStatusDisplay(inv);
              return (
                <TableRow key={inv.id}>
                  <TableCell>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(inv.id)}
                      onChange={() => onToggleRow(inv.id)}
                      aria-label={`Select invoice ${inv.invoiceNumber}`}
                      className={checkboxClass}
                    />
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-black">{formatListDate(inv.issueDate)}</TableCell>
                  <TableCell className="whitespace-nowrap">
                    <button
                      type="button"
                      onClick={() => onRowAction('view', inv)}
                      className="font-medium text-primary-s500 hover:underline"
                    >
                      {inv.invoiceNumber}
                    </button>
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-black">{inv.orderNumber ?? '—'}</TableCell>
                  <TableCell className="whitespace-nowrap text-black">{inv.customer?.fullName ?? '—'}</TableCell>
                  <TableCell className={`whitespace-nowrap font-medium ${style.className}`}>{style.label}</TableCell>
                  <TableCell className="whitespace-nowrap text-black">{formatListDate(inv.dueDate)}</TableCell>
                  <TableCell className="whitespace-nowrap text-black">{formatMoney(inv.totalAmount)}</TableCell>
                  <TableCell className="whitespace-nowrap text-black">{formatMoney(getInvoiceBalance(inv))}</TableCell>
                  <TableCell className="whitespace-nowrap text-black">{inv.customer?.companyName ?? '—'}</TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          className="flex h-8 w-8 items-center justify-center rounded-full text-lightGray transition-colors hover:bg-smoke1 hover:text-black"
                          aria-label="Row actions"
                        >
                          <MoreVertical size={16} />
                        </button>
                      </DropdownMenuTrigger>
                      {/* min-w is a floor, not a width: nowrap lets the panel grow so a
                          longer label can never wrap the way "Record Payment" used to. */}
                      <DropdownMenuContent align="end" className="min-w-[13rem]">
                        <DropdownMenuItem
                          className="gap-2.5 whitespace-nowrap bg-primary-s500 px-3 font-medium text-white focus:bg-blue-700 focus:text-white"
                          onClick={() => onRowAction('edit', inv)}
                        >
                          <SquarePen size={15} /> Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="gap-2.5 whitespace-nowrap px-3 [&>svg]:text-lightGray"
                          onClick={() => onRowAction('payment', inv)}
                        >
                          <CreditCard size={15} /> Record Payment
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="text-danger focus:bg-danger-light focus:text-danger gap-2.5 whitespace-nowrap px-3"
                          onClick={() => onRowAction('delete', inv)}
                        >
                          <Trash2 size={15} /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              );
            })}
      </TableBody>
    </Table>
  );
}
